import { Home, LayoutGrid, Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router";

import { useSlideBack } from "@/components/ui/slide-over-page";
import { EXPLORE_ENABLED } from "@/config/featureFlags";
import { hapticMedium, hapticLight } from "@/lib/haptics";
import mixpanel from "@/lib/mixpanel";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { useDesignPipelineStore } from "@/store/designPipelineStore";

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const slideBack = useSlideBack();

  const showOnPaths = [
    ...(EXPLORE_ENABLED ? ["/explore"] : []),
    "/my-shelf",
    "/profile",
    "/friends",
  ];

  const shouldShow =
    user &&
    showOnPaths.some(p =>
      p === "/my-shelf"
        ? location.pathname.startsWith("/my-shelf")
        : location.pathname === p,
    );

  if (!shouldShow) return null;

  const handleCreateClick = () => {
    hapticMedium();
    mixpanel.track("create_clicked", { source: "bottom_nav" });
    sessionStorage.removeItem("tinyapps-chat-session");
    useDesignPipelineStore.getState().reset();
    navigate("/create");
  };

  // Rendered left to right inside the pill: Shelf, Explore. Create is the
  // separate FAB alongside it, and Profile now lives in the header avatar.
  const items = [
    {
      key: "my-shelf",
      label: "Shelf",
      active: location.pathname.startsWith("/my-shelf"),
      onClick: () => {
        hapticLight();
        if (slideBack && location.pathname !== "/") {
          slideBack();
        } else {
          navigate("/my-shelf");
        }
      },
      icon: <LayoutGrid className="size-5" strokeWidth={2} />,
    },
    ...(EXPLORE_ENABLED
      ? [
          {
            key: "explore",
            label: "Explore",
            active: location.pathname === "/explore",
            onClick: () => {
              hapticLight();
              navigate("/explore");
            },
            icon: <Home className="size-5" strokeWidth={2} />,
          },
        ]
      : []),
  ];

  return (
    // The bar floats over the page, so the wrapper stays click-through and only
    // the pill and the FAB take pointer events.
    <nav className="pointer-events-none fixed right-0 bottom-0 left-0 z-50 md:hidden">
      <div className="mx-auto flex max-w-md items-center justify-center gap-3 px-5 pt-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="border-sl-200/70 pointer-events-auto flex items-center gap-1 rounded-full border bg-white p-1 shadow-[0_8px_24px_rgba(17,24,39,0.12)]">
          {items.map(item => (
            <button
              key={item.key}
              onClick={item.onClick}
              aria-current={item.active ? "page" : undefined}
              className={cn(
                "flex min-w-[6.5rem] flex-col items-center gap-0.5 rounded-full px-4 py-1 transition-colors",
                item.active ? "bg-sl-100 text-primary" : "text-sl-500",
              )}
            >
              {item.icon}
              <span className="text-xs leading-4 font-semibold tracking-[0.024px]">
                {item.label}
              </span>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={handleCreateClick}
          aria-label="Create"
          className="bg-primary pointer-events-auto flex size-14 shrink-0 items-center justify-center rounded-full text-white shadow-[0_8px_24px_rgba(135,79,255,0.4)] transition-transform active:scale-95"
        >
          <Plus className="size-7" strokeWidth={2.5} />
        </button>
      </div>
    </nav>
  );
}
